import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconCopy, IconCheck, IconChevronLeft, IconChevronRight, IconGift } from '@tabler/icons-react';
import { thunkFetchCouponForFeaturedDeals } from '../../../redux/slices/couponSlice';

const FeaturedCouponsPromo = () => {
  const dispatch = useDispatch();
  const [currentCouponIndex, setCurrentCouponIndex] = useState(0);
  const [copiedCode, setCopiedCode] = useState(null);
  const [hasFetchedCoupons, setHasFetchedCoupons] = useState(false);

  const { 
    featuredDeals, 
    clientLoading, 
    clientError 
  } = useSelector((state) => state.coupon);

  // Fetch featured coupons when component mounts
  useEffect(() => {
    if (!hasFetchedCoupons) {
      dispatch(thunkFetchCouponForFeaturedDeals())
        .finally(() => setHasFetchedCoupons(true));
    }
  }, [dispatch, hasFetchedCoupons]);

  // Auto-rotate coupons every 5 seconds
  useEffect(() => {
    if (featuredDeals && featuredDeals.length > 1) {
      const interval = setInterval(() => {
        setCurrentCouponIndex((prevIndex) => 
          (prevIndex + 1) % featuredDeals.length
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [featuredDeals]);

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const nextCoupon = () => {
    if (featuredDeals && featuredDeals.length > 1) {
      setCurrentCouponIndex((prevIndex) => 
        (prevIndex + 1) % featuredDeals.length
      );
    }
  };

  const prevCoupon = () => {
    if (featuredDeals && featuredDeals.length > 1) {
      setCurrentCouponIndex((prevIndex) => 
        prevIndex === 0 ? featuredDeals.length - 1 : prevIndex - 1
      );
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.coupon_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    } else if (coupon.coupon_type === 'fixed_amount') {
      return `$${coupon.discount_value} OFF`;
    } else if (coupon.coupon_type === 'free_shipping') {
      return 'FREE SHIPPING';
    }
    return 'SPECIAL OFFER';
  };

  const formatExpiry = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Show loading state
  if (clientLoading) {
    return (
      <div className="w-full md:w-auto flex flex-col md:flex-row items-start md:items-end gap-4">
        <div className="flex flex-col text-white animate-pulse">
          <div className="h-6 bg-white/20 rounded w-48 mb-1"></div>
          <div className="h-4 bg-white/20 rounded w-32"></div>
        </div>
      </div>
    );
  }

  // Show fallback if no coupons or error
  if (!featuredDeals || featuredDeals.length === 0 || clientError) {
    return (
      <div className="w-full md:w-auto flex flex-col md:flex-row items-start md:items-end gap-4">
        <div className="flex flex-col text-white">
          <span className="font-bold text-lg">🎉 Great Deals Available!</span>
          <div className="flex items-center gap-2 text-sm">
            <span>Check out our latest offers</span>
          </div>
        </div>
      </div>
    );
  }

  const currentCoupon = featuredDeals[currentCouponIndex];

  return (
    <div className="w-full md:w-auto flex flex-col md:flex-row items-start md:items-end gap-4">
      <div className="flex flex-col text-white min-w-0 flex-1">
        {/* Main coupon display */}
        <div className="flex items-center gap-2 mb-1">
          <IconGift size={20} className="text-yellow-300 animate-pulse" />
          <span className="font-bold text-lg">
            {formatDiscount(currentCoupon)} – {currentCoupon.name}
          </span>
          
          {/* Navigation arrows for multiple coupons */}
          {featuredDeals.length > 1 && (
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={prevCoupon}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Previous deal"
              >
                <IconChevronLeft size={16} />
              </button>
              <button
                onClick={nextCoupon}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Next deal"
              >
                <IconChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Coupon details */}
        <div className="flex items-center gap-2 text-sm">
          <span>Expires {formatExpiry(currentCoupon.end_date)}!</span>
          
          <button 
            onClick={() => handleCopyCode(currentCoupon.code)}
            className="flex items-center gap-1 text-yellow-300 hover:text-yellow-200 transition-colors"
          >
            {copiedCode === currentCoupon.code ? (
              <IconCheck size={16} />
            ) : (
              <IconCopy size={16} />
            )}
            <span className="bg-[#00148c] border border-yellow-300 px-2 py-1 rounded-sm text-xs font-medium hover:bg-[#00148c]/90 transition-colors">
              {currentCoupon.code}
            </span>
          </button>

          {/* Coupon indicator dots */}
          {featuredDeals.length > 1 && (
            <div className="flex items-center gap-1 ml-2">
              {featuredDeals.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentCouponIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentCouponIndex 
                      ? 'bg-yellow-300' 
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Additional info for special conditions */}
        {currentCoupon.minimum_order_amount && parseFloat(currentCoupon.minimum_order_amount) > 0 && (
          <div className="text-xs text-yellow-200 mt-1">
            Min. order: ${currentCoupon.minimum_order_amount}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedCouponsPromo;